import React, { useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Sparkles, Star, Check, MapPin, Crown, BadgeCheck } from "lucide-react";
import { toast } from "sonner";

const TIER_META = {
  standard: { label: "Standard", color: "#00E676", icon: MapPin, desc: "Visibile nella lista 'Scopri tutte le attività' del comune." },
  premium: { label: "Premium", color: "#7DF9FF", icon: BadgeCheck, desc: "In evidenza direttamente sulla mappa del comune." },
  sponsor: { label: "Sponsor", color: "#FFD700", icon: Crown, desc: "Sempre in primo piano sulla mappa, con effetti grafici dedicati." },
};

export default function VisibilityPanel() {
  const [data, setData] = useState(null);
  const [buying, setBuying] = useState("");

  const load = useCallback(async () => {
    try { const { data } = await api.get("/business/visibility"); setData(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  // Ritorno dal checkout Stripe: verifica lo stato del pagamento.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sess = params.get("vis_session");
    if (!sess) return;
    let tries = 0;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sess}`);
        if (data.payment_status === "paid") { toast.success("Pagamento completato! Visibilità attivata 🎉"); await load(); window.history.replaceState({}, "", "/business/visibility"); return; }
        if (["failed", "cancelled"].includes(data.payment_status)) { toast.error("Pagamento non riuscito."); window.history.replaceState({}, "", "/business/visibility"); return; }
      } catch (e) { /* retry */ }
      if (tries++ < 6) setTimeout(poll, 1500);
    };
    poll();
  }, [load]);

  const buy = async (planId) => {
    setBuying(planId);
    try {
      const { data } = await api.post("/business/visibility/purchase", { plan: planId, origin_url: window.location.origin });
      if (data.checkout_url) window.location.href = data.checkout_url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setBuying(""); }
  };

  if (!data) return <div className="text-zinc-500 py-16 text-center" data-testid="visibility-loading">Caricamento…</div>;
  const cur = TIER_META[data.tier] || TIER_META.standard;
  const CurIcon = cur.icon;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="visibility-panel">
      <div>
        <h2 className="font-display font-bold text-2xl flex items-center gap-2"><Sparkles className="h-6 w-6 text-[#00E676]" /> Visibilità sulla mappa</h2>
        <p className="text-sm text-zinc-400 mt-1">Più visibilità = più clienti. Fatti trovare in primo piano dai clienti del tuo comune.</p>
      </div>

      {/* Stato attuale */}
      <div className="rounded-2xl border p-5 flex items-center justify-between flex-wrap gap-3" style={{ borderColor: `${cur.color}55`, background: `${cur.color}12` }} data-testid="visibility-current">
        <div className="flex items-center gap-3">
          <div className="h-12 w-12 rounded-xl flex items-center justify-center" style={{ background: `${cur.color}22`, border: `1px solid ${cur.color}55` }}><CurIcon className="h-6 w-6" style={{ color: cur.color }} /></div>
          <div>
            <div className="text-xs text-zinc-400">Livello attuale</div>
            <div className="font-display font-bold text-lg" style={{ color: cur.color }}>{cur.label}</div>
          </div>
        </div>
        <div className="text-right">
          <div className="text-xs text-zinc-500 max-w-xs">{cur.desc}</div>
          {data.tier !== "standard" && data.expires_at && <div className="text-[11px] text-zinc-400 mt-1">Scade il {new Date(data.expires_at).toLocaleDateString("it-IT")}{data.source === "manual" ? " · assegnato dalla centrale" : ""}</div>}
        </div>
      </div>

      {/* Piani acquistabili */}
      <div className="grid sm:grid-cols-2 gap-4">
        {data.plans.map((p) => {
          const meta = TIER_META[p.tier];
          const Icon = meta.icon;
          const active = data.tier === p.tier;
          return (
            <div key={p.id} data-testid={`visibility-plan-${p.id}`} className="rounded-2xl border p-5 flex flex-col" style={{ borderColor: `${meta.color}40`, background: "#131316" }}>
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5" style={{ color: meta.color }} />
                <h3 className="font-display font-bold text-lg" style={{ color: meta.color }}>{p.name}</h3>
                {p.tier === "sponsor" && <span className="ml-auto text-[10px] font-bold text-[#FFD700] bg-[#FFD700]/15 border border-[#FFD700]/40 rounded-full px-2 py-0.5">TOP</span>}
              </div>
              <div className="mt-3 flex items-baseline gap-1"><span className="font-display font-black text-3xl">€{p.price.toFixed(2)}</span><span className="text-sm text-zinc-500">/ {p.days} giorni</span></div>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f, i) => <li key={i} className="flex items-start gap-2 text-sm text-zinc-300"><Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: meta.color }} /> {f}</li>)}
              </ul>
              <Button data-testid={`visibility-buy-${p.id}`} disabled={buying === p.id} onClick={() => buy(p.id)}
                className="mt-5 rounded-full font-semibold text-black h-11" style={{ background: meta.color }}>
                {buying === p.id ? "Reindirizzamento…" : active ? "Rinnova / estendi" : `Attiva ${p.name}`}
              </Button>
            </div>
          );
        })}
      </div>
      <p className="text-[11px] text-zinc-500 flex items-center gap-1"><Star className="h-3 w-3" /> Pagamento sicuro con Stripe. La visibilità si attiva subito dopo il pagamento e dura {data.plans[0]?.days || 30} giorni.</p>
    </div>
  );
}
